/* ============================================================================
   ALT-COL-005 — Collection UI controller (progressive enhancement).
   Drives the unified Filter & Sort drawer, radio sort inputs, collapsible
   facet groups, active chips, and no-reload filtering via Shopify's
   Section Rendering API (?section_id=...).
   ========================================================================== */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function AltCollectionUI(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id');
    this.baseUrl = root.getAttribute('data-collection-url') || window.location.pathname;
    this.form = root.querySelector('[data-filter-form]');
    this.drawer = root.querySelector('[data-filter-drawer]');
    this.gridWrap = root.querySelector('#ProductGridContainer');
    this.openerBtn = root.querySelector('[data-filter-open]');
    this.lastFocused = null;

    this.onFilterInput = debounce(this.submitFilters.bind(this), 450);
    this.bind();
  }

  AltCollectionUI.prototype.bind = function () {
    var self = this;

    // Drawer open / close
    if (this.openerBtn) {
      this.openerBtn.addEventListener('click', function () {
        self.openDrawer();
      });
    }
    this.root.querySelectorAll('[data-filter-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        self.closeDrawer();
      });
    });

    // Filter and Sort inputs inside drawer form
    if (this.form) {
      this.form.addEventListener('change', function (e) {
        if (e.target.matches('[data-filter-input]')) {
          if (e.target.matches('[data-price-min], [data-price-max]')) self.clampPrice();
          if (e.target.matches('[data-sort-radio]')) self.updateSortRadioStyles(e.target);
          self.updateBadge();
          self.onFilterInput();
        }
      });

      // Apply button (submit) closes the drawer and forces an immediate update
      this.form.addEventListener('submit', function (e) {
        e.preventDefault();
        self.submitFilters();
        self.closeDrawer();
      });
    }

    // Chips + clear-all (event delegation so swapped-in chips keep working)
    this.root.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-chip]');
      var clear = e.target.closest('[data-clear-all]');
      if (chip) {
        e.preventDefault();
        self.removeChip(chip);
      } else if (clear) {
        e.preventDefault();
        self.clearAll();
      }
    });

    // Global: Esc closes drawer, Tab focus trap
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        if (self.drawer && self.drawer.classList.contains('is-open')) self.closeDrawer();
      }
      if (e.key === 'Tab' && self.drawer && self.drawer.classList.contains('is-open')) {
        self.trapFocus(e);
      }
    });

    window.addEventListener('popstate', function () {
      self.render(window.location.search.replace(/^\?/, ''), false);
    });

    this.syncToolbarToHeader();
  };

  AltCollectionUI.prototype.updateSortRadioStyles = function (checkedInput) {
    if (!this.form) return;
    this.form.querySelectorAll('.alt-sort-radio').forEach(function (label) {
      var radio = label.querySelector('[data-sort-radio]');
      if (radio) {
        label.classList.toggle('is-selected', radio.checked);
      }
    });
  };

  /* Glue the sticky toolbar to the header's live bottom edge so it hides/reveals WITH
     the header (which transforms translateY(-100%) on scroll-down) instead of leaving a
     gap. Falls back to the CSS `top: var(--header-height)` if the header isn't found. */
  AltCollectionUI.prototype.syncToolbarToHeader = function () {
    var toolbar = this.root.querySelector('[data-collection-toolbar]');
    var header = document.querySelector('.section-header');
    if (!toolbar || !header) return;

    var followUntil = 0;
    var running = false;
    function apply() {
      toolbar.style.top = Math.max(0, header.getBoundingClientRect().bottom) + 'px';
    }
    function loop() {
      apply();
      if (performance.now() < followUntil) requestAnimationFrame(loop);
      else running = false;
    }
    function kick() {
      followUntil = performance.now() + 420; // covers the header's ~0.28s transform transition
      if (!running) { running = true; requestAnimationFrame(loop); }
    }
    window.addEventListener('scroll', kick, { passive: true });
    window.addEventListener('resize', kick);
    new MutationObserver(kick).observe(header, { attributes: true, attributeFilter: ['class'] });
    apply();
  };

  /* ---- Drawer ------------------------------------------------------------ */
  AltCollectionUI.prototype.openDrawer = function () {
    if (!this.drawer) return;
    this.lastFocused = document.activeElement;
    this.drawer.classList.add('is-open');
    if (this.openerBtn) this.openerBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    var first = this.drawer.querySelector(FOCUSABLE);
    if (first) first.focus();
  };

  AltCollectionUI.prototype.closeDrawer = function () {
    if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
    this.drawer.classList.remove('is-open');
    if (this.openerBtn) this.openerBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    if (this.lastFocused) this.lastFocused.focus();
  };

  AltCollectionUI.prototype.trapFocus = function (e) {
    var panel = this.drawer.querySelector('.alt-filter-drawer__panel');
    var nodes = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (n) {
      return n.offsetParent !== null;
    });
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  /* ---- Params + fetch ---------------------------------------------------- */
  AltCollectionUI.prototype.buildParams = function (sortOverride) {
    var params = new URLSearchParams();
    if (this.form) {
      var data = new FormData(this.form);
      data.forEach(function (val, key) {
        if (val !== null && String(val).trim() !== '') params.append(key, val);
      });
    }
    if (sortOverride) params.set('sort_by', sortOverride);
    return params.toString();
  };

  AltCollectionUI.prototype.submitFilters = function (sortOverride) {
    this.render(this.buildParams(sortOverride), true);
  };

  AltCollectionUI.prototype.removeChip = function (chip) {
    var self = this;
    if (chip.hasAttribute('data-chip-price')) {
      this.form.querySelectorAll('[data-price-min], [data-price-max]').forEach(function (i) {
        i.value = '';
      });
    } else {
      var name = chip.getAttribute('data-param-name');
      var value = chip.getAttribute('data-value');
      if (this.form && name) {
        this.form.querySelectorAll('input[name="' + name + '"]').forEach(function (input) {
          if (input.value === value) input.checked = false;
        });
      }
    }
    this.updateBadge();
    var href = chip.getAttribute('href') || '';
    var q = href.indexOf('?') > -1 ? href.slice(href.indexOf('?') + 1) : '';
    this.render(q || this.buildParams(), true);
  };

  AltCollectionUI.prototype.clearAll = function () {
    if (this.form) {
      this.form.querySelectorAll('[data-filter-input]').forEach(function (input) {
        if (input.type === 'checkbox') input.checked = false;
        else if (input.type === 'text') input.value = '';
      });
    }
    this.updateBadge();
    var selectedSort = this.form ? this.form.querySelector('[data-sort-radio]:checked') : null;
    this.render(selectedSort && selectedSort.value ? 'sort_by=' + encodeURIComponent(selectedSort.value) : '', true);
  };

  AltCollectionUI.prototype.clampPrice = function () {
    if (!this.form) return;
    var minEl = this.form.querySelector('[data-price-min]');
    var maxEl = this.form.querySelector('[data-price-max]');
    var range = this.form.querySelector('[data-price-range]');
    var rangeMax = range ? parseFloat(range.getAttribute('data-range-max')) : NaN;
    var min = minEl && minEl.value !== '' ? parseFloat(minEl.value) : null;
    var max = maxEl && maxEl.value !== '' ? parseFloat(maxEl.value) : null;
    if (min !== null && min < 0) minEl.value = 0;
    if (!isNaN(rangeMax) && max !== null && max > rangeMax) maxEl.value = rangeMax;
    if (min !== null && max !== null && min > max) minEl.value = maxEl.value;
  };

  AltCollectionUI.prototype.updateBadge = function () {
    if (!this.form) return;
    var count = this.form.querySelectorAll('input[type="checkbox"][data-filter-input]:checked').length;
    var priceSet = false;
    this.form.querySelectorAll('[data-price-min], [data-price-max]').forEach(function (i) {
      if (i.value !== '') priceSet = true;
    });
    if (priceSet) count += 1;
    var badge = this.root.querySelector('[data-filter-count]');
    if (badge) {
      badge.textContent = count;
      badge.classList.toggle('is-empty', count === 0);
    }
  };

  AltCollectionUI.prototype.setLoading = function (on) {
    if (this.gridWrap) this.gridWrap.classList.toggle('loading', on);
    var count = this.root.querySelector('#ProductCount');
    if (count) count.classList.toggle('loading', on);
  };

  AltCollectionUI.prototype.render = function (searchParams, updateHistory) {
    var self = this;
    var url = this.baseUrl + '?section_id=' + this.sectionId + (searchParams ? '&' + searchParams : '');
    var publicUrl = this.baseUrl + (searchParams ? '?' + searchParams : '');
    this.setLoading(true);

    fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('Section render failed: ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        self.swap(doc, '#ProductGridContainer');
        self.swap(doc, '#ProductCount');
        self.swap(doc, '[data-active-filters]');

        // Sync the filtered product count onto the live count container + apply button
        var srcCount = doc.querySelector('#ProductCount');
        if (srcCount && srcCount.dataset.productCount != null) {
          var liveCount = self.root.querySelector('#ProductCount');
          if (liveCount) liveCount.dataset.productCount = srcCount.dataset.productCount;
          var applyCount = self.root.querySelector('[data-results-count]');
          if (applyCount) applyCount.textContent = srcCount.dataset.productCount;
        }
        self.gridWrap = self.root.querySelector('#ProductGridContainer');
        if (updateHistory) history.pushState({ altcol: true }, '', publicUrl);
        self.setLoading(false);
        self.announce();
        document.dispatchEvent(new CustomEvent('alt:collection:rendered'));
      })
      .catch(function () {
        // Fallback: full navigation preserves correctness if the fetch layer fails.
        window.location.href = publicUrl;
      });
  };

  AltCollectionUI.prototype.swap = function (doc, selector) {
    var next = doc.querySelector(selector);
    var current = this.root.querySelector(selector);
    if (next && current) current.innerHTML = next.innerHTML;
  };

  AltCollectionUI.prototype.announce = function () {
    var count = this.root.querySelector('#ProductCount');
    if (count) count.setAttribute('aria-live', 'polite');
  };

  function init() {
    document.querySelectorAll('[data-collection-ui]').forEach(function (root) {
      if (!root.__altCollectionUI) root.__altCollectionUI = new AltCollectionUI(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after the theme editor re-renders the section
  document.addEventListener('shopify:section:load', init);
})();
